import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@akostm/orbit-ui';
import {
  faEdit,
  faPlus,
  faTrash,
  faEye,
  faEyeSlash,
  faCopy,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Dialog } from '@angular/cdk/dialog';
import { ApiKeysDialogComponent } from './api-keys-dialog/api-keys-dialog.component';
import { ApiKeysService } from './api-keys.service';
import { ApiKeyListItem } from '@clientwire/api';
import { Clipboard } from '@angular/cdk/clipboard';
import { signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@frontend/shared';

@Component({
  selector: 'lib-api-keys',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './api-keys.component.html',
  styleUrl: './api-keys.component.scss',
})
export class ApiKeysComponent implements OnInit {
  apiKeysService: ApiKeysService = inject(ApiKeysService);
  private clipboard: Clipboard = inject(Clipboard);
  private translateService: TranslateService = inject(TranslateService);

  loading = this.apiKeysService.loading;
  apiKeys = this.apiKeysService.apiKeys;
  showToken = signal(false);

  faEdit = faEdit;
  faPlus = faPlus;
  faTrash = faTrash;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faCopy = faCopy;
  faClose = faXmark;

  constructor(private dialog: Dialog) {}

  ngOnInit(): void {
    this.apiKeysService.loadApiKeys();
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(ApiKeysDialogComponent, {
      data: {
        title: 'SETTINGS.API_KEYS.DIALOG.CREATE.TITLE',
        error: this.apiKeysService.error,
      },
    });
    dialogRef.componentInstance?.dialogButtonClicked.subscribe(async (form) => {
      if (form.eventType === 'SAVE' && form.data) {
        const response = await this.apiKeysService.createApiKey(form.data);
        if (response?.apiKey) {
          dialogRef.close();
        }
      }
      if (form.eventType === 'CANCEL') {
        this.apiKeysService.clearErrors();
        dialogRef.close();
      }
    });
  }

  onEdit(key: ApiKeyListItem): void {
    const dialogRef = this.dialog.open(ApiKeysDialogComponent, {
      data: {
        title: 'SETTINGS.API_KEYS.DIALOG.EDIT.TITLE',
        apiKey: key,
        error: this.apiKeysService.error,
      },
    });
    dialogRef.componentInstance?.dialogButtonClicked.subscribe(async (form) => {
      if (form.eventType === 'SAVE' && form.data) {
        await this.apiKeysService.updateApiKey({
          keyId: key.keyId,
          requestBody: {
            name: form.data.name,
            expires_at: form.data.expiresAt,
          },
        });
        if (!this.apiKeysService.error()) {
          dialogRef.close();
        }
      }
      if (form.eventType === 'CANCEL') {
        this.apiKeysService.clearErrors();
        dialogRef.close();
      }
    });
  }

  toggleTokenVisibility(): void {
    this.showToken.set(!this.showToken());
  }

  copyToken(token: string): void {
    this.clipboard.copy(token);
  }

  closeTokenAlert(): void {
    this.apiKeysService.clearErrors();
  }

  async deleteApiKey(apiKey: ApiKeyListItem): Promise<void> {
    const dialogRef = this.dialog.open<any>(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('SETTINGS.API_KEYS.CONFIRM_DELETE_TITLE'),
        confirmLabel: this.translateService.instant('SETTINGS.API_KEYS.DELETE'),
        message: this.translateService.instant('SETTINGS.API_KEYS.DELETE_MESSAGE'),
        isConfirmation: true,
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.apiKeysService.deleteApiKey(apiKey.keyId);
      }
    });
  }
}
